import { ArrowRight } from 'lucide-react';
import { useState } from 'react';
import type { MetaFunction } from 'react-router';
import { Link } from 'react-router';
import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import {
  PRODUCT_CATEGORY_LABELS,
  productTemplates,
} from '~/lib/product-templates';
import { cn } from '~/lib/utils';

export const meta: MetaFunction = () => [
  { title: 'Products — Specly' },
  {
    name: 'description',
    content:
      'Pick a real product like Slack or Notion and see the feature specs behind it.',
  },
];

const ALL = 'all';

export default function ProductsIndex() {
  const categories = [
    ALL,
    ...([...new Set(productTemplates.map((p) => p.category))] as string[]),
  ];
  const [active, setActive] = useState<string>(ALL);

  const visible =
    active === ALL
      ? productTemplates
      : productTemplates.filter((p) => p.category === active);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Header */}
      <div className="mb-10">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          ← All specs
        </Link>
        <h1 className="text-4xl font-bold mb-3">Start from a real product</h1>
        <p className="text-lg text-muted-foreground max-w-2xl">
          Pick a product you know — like Slack, Notion, or Stripe — and see
          exactly which feature specs power it. Use it as your starting point.
        </p>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2 mb-8">
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => setActive(cat)}
            className={cn(
              'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
              active === cat
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80',
            )}
          >
            {cat === ALL ? 'All' : (PRODUCT_CATEGORY_LABELS[cat] ?? cat)}
          </button>
        ))}
      </div>

      {/* Product grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.map((product) => (
          <Link key={product.slug} to={`/products/${product.slug}`}>
            <Card className="h-full p-5 hover:shadow-md hover:ring-1 hover:ring-primary/20 transition-all cursor-pointer group">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div>
                  <h2 className="font-semibold text-base group-hover:text-primary transition-colors">
                    {product.name}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {PRODUCT_CATEGORY_LABELS[product.category] ??
                      product.category}
                  </p>
                </div>
                <ArrowRight className="size-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-0.5" />
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {product.tagline}
              </p>
            </Card>
          </Link>
        ))}
      </div>

      {/* Bottom CTA */}
      <div className="mt-12 text-center">
        <p className="text-muted-foreground mb-3 text-sm">
          Don't see your product? Browse all spec archetypes instead.
        </p>
        <Button variant="outline" render={<Link to="/" />}>
          Browse all specs
        </Button>
      </div>
    </div>
  );
}
